import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getAuthContext } from '../../utils/jwt';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';

// Interface for billing history item from API
interface Invoice {
  id: string;
  tenantId: string;
  invoiceId: string;
  planCode: string;
  amountCents: number;
  currency: string;
  status: string;
  createdAt: string;
  providerRef: string;
}

// Interface for component props
interface BillingHistoryProps {
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  setExportPDF: React.Dispatch<React.SetStateAction<(() => void) | null>>;
}

// Component to display and manage billing history table
const BillingHistory: React.FC<BillingHistoryProps> = ({
  setSelected,
  setExportPDF,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [localSelected, setLocalSelected] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);
  const itemsPerPage = 6;

  const generateIdempotencyKey = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (amountCents: number, currency: string): string => {
    const amount = amountCents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const getPlanDisplayName = (planCode: string): string => {
    const planMap: Record<string, string> = {
      'FREE': 'Free',
      'PREMIUM': 'Premium',
      'ENTERPRISE': 'Enterprise',
    };
    return planMap[planCode] || planCode;
  };

  const getStatusColor = (status: string): string => {
    const statusMap: Record<string, string> = {
      'PAID': 'bg-green-100 text-green-600',
      'PENDING': 'bg-yellow-100 text-yellow-600',
      'FAILED': 'bg-red-100 text-red-600',
      'CANCELLED': 'bg-gray-100 text-gray-600',
      'REFUNDED': 'bg-blue-100 text-blue-600',
    };
    return statusMap[status] || 'bg-gray-100 text-gray-600';
  };

  const getStatusDisplay = (status: string): string => {
    const statusMap: Record<string, string> = {
      'PAID': 'Paid',
      'PENDING': 'Pending',
      'FAILED': 'Failed',
      'CANCELLED': 'Cancelled',
      'REFUNDED': 'Refunded',
    };
    return statusMap[status] || status;
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const idempotencyKey = generateIdempotencyKey();

      const response = await fetch(`${BASE_URL}/api/settings/billing/invoices`, {
        method: 'GET',
        headers: {
          'X-Idempotency-Key': idempotencyKey,
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch invoices: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'OK' && Array.isArray(data.data)) {
        setInvoices(data.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load invoices');
      // console.error('Fetch invoices error:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoicePDF = async (invoice: Invoice) => {
    try {
      setDownloadingPdf(invoice.invoiceId);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const idempotencyKey = generateIdempotencyKey();

      const response = await fetch(`${BASE_URL}/api/settings/billing/invoices/${invoice.invoiceId}/pdf`, {
        method: 'GET',
        headers: {
          'X-Idempotency-Key': idempotencyKey,
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/pdf',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download PDF: ${response.status}`);
      }

      // Get the PDF as blob
      const blob = await response.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Invoice ${invoice.invoiceId} downloaded successfully`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to download PDF');
      // console.error('Download PDF error:', error);
    } finally {
      setDownloadingPdf(null);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Calculate pagination details
  const totalPages = Math.ceil(invoices.length / itemsPerPage);
  const paginatedData = invoices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Calculate start and end items
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, invoices.length);

  // Handle page change with smooth scroll
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Update parent component with selected invoice IDs
  useEffect(() => {
    setSelected(localSelected);
  }, [localSelected, setSelected]);

  // Set up PDF export function
  useEffect(() => {
    const exportPDF = () => {
      const rows = invoices.filter((inv) => localSelected.includes(inv.invoiceId));
      const doc = new jsPDF();
      autoTable(doc, {
        head: [['Invoice ID', 'Date', 'Plan Name', 'Amount', 'Status']],
        body: rows.map((r) => [
          r.invoiceId,
          formatDate(r.createdAt),
          getPlanDisplayName(r.planCode),
          formatAmount(r.amountCents, r.currency),
          getStatusDisplay(r.status),
        ]),
      });
      doc.save('billing-history.pdf');
    };
    setExportPDF(() => exportPDF);
  }, [localSelected, setExportPDF, invoices]);

  // Toggle select all checkboxes
  // const toggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (e.target.checked) {
  //     setLocalSelected(paginatedData.map((inv) => inv.invoiceId));
  //   } else {
  //     setLocalSelected([]);
  //   }
  // };

  // Toggle selection for individual invoice
  // const toggleSelect = (invoiceId: string) => {
  //   setLocalSelected((prev) =>
  //     prev.includes(invoiceId) ? prev.filter((n) => n !== invoiceId) : [...prev, invoiceId]
  //   );
  // };

  // Export single row to PDF
  // const exportRowPDF = (invoice: Invoice) => {
  //   const doc = new jsPDF();
  //   autoTable(doc, {
  //     head: [['Invoice ID', 'Date', 'Plan Name', 'Amount', 'Status']],
  //     body: [[
  //       invoice.invoiceId,
  //       formatDate(invoice.createdAt),
  //       getPlanDisplayName(invoice.planCode),
  //       formatAmount(invoice.amountCents, invoice.currency),
  //       getStatusDisplay(invoice.status),
  //     ]],
  //   });
  //   doc.save(`${invoice.invoiceId}.pdf`);
  // };

  if (loading) {
    return (
      <div className='flex justify-center items-center py-12'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#00899E]'></div>
      </div>
    );
  }

  return (
    <div className='bg-white rounded-md'>
      {/* Table for displaying billing history */}
      <div>
        <table className='w-full text-left mt-4 border border-gray-200'>
          <thead className='bg-gray-100 text-[12px] text-[#657584]'>
            <tr>
              {/* <th className='px-4 py-3'>
                <div className='flex items-center justify-start'>
                  <input
                    type='checkbox'
                    onChange={toggleSelectAll}
                    checked={
                      localSelected.length === paginatedData.length &&
                      paginatedData.length > 0
                    }
                    className='w-4 h-4 rounded'
                  />
                </div>
              </th> */}
              <th className='px-4 py-3 font-normal'>INVOICE ID</th>
              <th className='px-4 py-3 font-normal'>DATE</th>
              <th className='px-4 py-3 font-normal'>PLAN NAME</th>
              <th className='px-4 py-3 font-normal'>AMOUNT</th>
              <th className='px-4 py-3 font-normal'>STATUS</th>
              <th className='px-4 py-3 font-normal'>ACTIONS</th>
            </tr>
          </thead>
          <tbody className='text-gray-700 text-sm'>
            {paginatedData.map((invoice) => (
              <tr key={invoice.id} className='border-b hover:bg-gray-50'>
                {/* <td className='px-4 py-4'>
                  <input
                    type='checkbox'
                    checked={localSelected.includes(invoice.invoiceId)}
                    onChange={() => toggleSelect(invoice.invoiceId)}
                    className='w-4 h-4 rounded'
                  />
                </td> */}
                <td className='px-4 py-4'>{invoice.invoiceId}</td>
                <td className='px-4 py-4'>{formatDate(invoice.createdAt)}</td>
                <td className='px-4 py-4'>{getPlanDisplayName(invoice.planCode)}</td>
                <td className='px-4 py-4'>{formatAmount(invoice.amountCents, invoice.currency)}</td>
                <td className='px-4 py-4'>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      invoice.status
                    )}`}
                  >
                    {getStatusDisplay(invoice.status)}
                  </span>
                </td>
                <td className='px-4 py-4 text-[#00899E] text-[14px]'>
                  {invoice.status === 'PAID' && (
                    // <button
                    //   onClick={() => exportRowPDF(invoice)}
                    //   className='hover:underline'
                    // >
                    //   Download PDF
                    // </button>
                    <button
                      onClick={() => downloadInvoicePDF(invoice)}
                      disabled={downloadingPdf === invoice.invoiceId}
                      className='hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      {downloadingPdf === invoice.invoiceId ? 'Downloading...' : 'Download PDF'}
                    </button>
                  )}
                  {invoice.status === 'PENDING' && (
                    <button className='hover:underline'>View</button>
                  )}
                  {invoice.status === 'FAILED' && (
                    <button className='hover:underline'>Retry Payment</button>
                  )}
                </td>
              </tr>
            ))}
            {paginatedData.length === 0 && (
              <tr>
                <td colSpan={6} className='px-6 py-6 text-center text-gray-500'>
                  No billing records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      {invoices.length > 0 && (
        <div className='flex items-center justify-between px-6 py-4 text-sm text-gray-500 border-t border-gray-200 mt-4'>
          <span>
            Showing {startItem} to {endItem} of {invoices.length} invoices
          </span>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {(() => {
                const pages = [];
                const total = totalPages;
                const current = currentPage;
                const delta = 1;

                pages.push(1);

                let rangeStart = Math.max(2, current - delta);
                let rangeEnd = Math.min(total - 1, current + delta);

                if (rangeStart > 2) {
                  pages.push('...');
                }

                for (let i = rangeStart; i <= rangeEnd; i++) {
                  pages.push(i);
                }

                if (rangeEnd < total - 1) {
                  pages.push('...');
                }

                if (total > 1) {
                  pages.push(total);
                }

                return pages.map((page, index) => {
                  if (page === '...') {
                    return (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-3 py-2 text-sm text-gray-500"
                      >
                        …
                      </span>
                    );
                  }

                  const pageNumber = page as number;
                  const isActive = pageNumber === current;

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                        ? 'bg-orange-500 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {pageNumber}
                    </button>
                  );
                });
              })()}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BillingHistory;